import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FlexibleDashboard from '../components/FlexibleDashboard';
import useImpersonation from '../hooks/useImpersonation';
import { createApiUrl } from '../utils/apiConfig';
import OverviewTab from '../components/StudentDashboardTabs/OverviewTab';
import MissionsTab from '../components/StudentDashboardTabs/MissionsTab';
import ProgressTab from '../components/StudentDashboardTabs/ProgressTab';
import BadgesTab from '../components/StudentDashboardTabs/BadgesTab';
import MessagesTab from '../components/StudentDashboardTabs/MessagesTab';
import AssignedLessonsTab from '../components/StudentDashboardTabs/AssignedLessonsTab';
import AssignedQuizzesTab from '../components/StudentDashboardTabs/AssignedQuizzesTab';
import Toast from '../components/Toast';
import ImpersonationBanner from '../components/ImpersonationBanner';
import './StudentDashboard.css';

const StudentDashboard = () => {
  const [user, setUser] = useState(null);
  const [missions, setMissions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [toast, setToast] = useState(null);
  const [assignedLessons, setAssignedLessons] = useState([]);
  const [loadingAssignedLessons, setLoadingAssignedLessons] = useState(false);
  const [assignedQuizzes, setAssignedQuizzes] = useState([]);
  const [loadingAssignedQuizzes, setLoadingAssignedQuizzes] = useState(false);
  const { isImpersonating: _isImpersonating, impersonationData: _impersonationData, stopImpersonation: _stopImpersonation, loading: _impersonationLoading } = useImpersonation();

  // Calculate unread message count (messages without replies that need student response)
  const unreadMessageCount = messages.filter(msg => !msg.reply).length;
  
  // Calculate assigned missions count (only not started missions)
  const assignedMissionCount = missions.filter(mission => 
    mission.status === 'assigned'
  ).length;

  // Calculate unread assigned lessons count (lessons that haven't been viewed)
  const unreadAssignedLessonsCount = assignedLessons.filter(lesson => lesson.status === 'assigned').length;

  // Calculate assigned quizzes count (quizzes that haven't been started)
  const assignedQuizzesCount = assignedQuizzes.filter(quiz => quiz.assignmentStatus === 'assigned').length;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleViewProgress = () => {
    setActiveTab('progress');
  };

  const handleViewMissions = () => {
    setActiveTab('missions');
  };

  const handleViewLessons = () => {
    setActiveTab('assignedlessons');
  };

  const _handleRefreshData = async () => {
    try {
      await refreshUserData();
      showToast('Data refreshed!', 'success');
    } catch (error) {
      showToast('Failed to refresh data', 'error');
    }
  };

  // Function to mark mission as viewed
  const handleMissionViewed = async (missionId) => {
    try {
      
      const response = await axios.post(createApiUrl(`/api/mission-assignments/${missionId}/mark-viewed`));
      
      if (response.data.success) {
        // Update the mission in the local state to mark it as viewed
        setMissions(prevMissions => 
          prevMissions.map(mission => 
            mission._id === missionId 
              ? { ...mission, viewed: true, viewedAt: new Date().toISOString() }
              : mission
          )
        );
        
      }
    } catch (error) {
            showToast('Failed to mark mission as viewed', 'error');
    }
  };

  // Function to start mission (update status to in-progress)
  const handleStartMission = async (missionId) => {
    try {
      const response = await axios.post(createApiUrl(`/api/mission-assignments/${missionId}/start`));
      
      if (response.data.success) {
        // Update the mission in the local state to mark it as in-progress
        setMissions(prevMissions => 
          prevMissions.map(mission => 
            mission._id === missionId 
              ? { ...mission, status: 'in-progress', viewed: true, viewedAt: new Date().toISOString() }
              : mission
          )
        );
        showToast('Mission started!', 'success');
      }
    } catch (error) {
      console.error('Error starting mission:', error);
      showToast('Failed to start mission', 'error');
    }
  };

  // Helper function to show toast
  const showToast = (message, type = 'success', duration = 3000) => {
    setToast({ message, type, duration });
  };

  // Message handling functions
  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
    // Force a re-render check
    setTimeout(() => {
    }, 100);
  };

  const handleSendReply = async (messageId) => {
    if (!replyText.trim()) return;

    setSendingReply(true);
    try {
      const response = await axios.post(createApiUrl('/api/student/send-reply'), {
        messageId: messageId,
        reply: replyText
      });

      if (response.data.success) {
        // Update the message in the list with the reply
        setMessages(messages.map(msg => 
          msg._id === messageId 
            ? { ...msg, reply: replyText, repliedAt: new Date().toISOString() }
            : msg
        ));
        setReplyText('');
        setShowMessageModal(false);
        setSelectedMessage(null);
        showToast('Reply sent successfully!');
      }
    } catch (error) {
            showToast('Failed to send reply. Please try again.', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedMessage(null);
    setReplyText('');
  };


  // Fetch assigned lessons
  const fetchAssignedLessons = async () => {
    setLoadingAssignedLessons(true);
    try {
      const response = await axios.get(createApiUrl('/api/lesson-assignments/student'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAssignedLessons(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching assigned lessons:', error);
    } finally {
      setLoadingAssignedLessons(false);
    }
  };

  // Fetch assigned quizzes
  const fetchAssignedQuizzes = async () => {
    setLoadingAssignedQuizzes(true);
    try {
      const response = await axios.get(createApiUrl(`/api/quiz-assignments/student/${user?._id}`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Process quiz assignments for this student
        const studentQuizzes = response.data.assignments
          .map(assignment => {
            return {
              ...assignment.quizId,
              assignmentId: assignment._id, // Add assignment ID for status updates
              assignmentStatus: assignment.status || 'assigned',
              assignedAt: assignment.assignedDate || assignment.createdAt,
              completedAt: assignment.completedDate,
              dueDate: assignment.dueDate,
              score: assignment.score,
              percentage: assignment.percentage,
              attempts: assignment.attempts || 0,
              maxAttempts: assignment.quizId.settings?.maxAttempts || 1
            };
          })
          .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
        
        setAssignedQuizzes(studentQuizzes);
      } else {
        setAssignedQuizzes([]);
      }
    } catch (error) {
      console.error('Error fetching assigned quizzes:', error);
      setAssignedQuizzes([]);
    } finally {
      setLoadingAssignedQuizzes(false);
    }
  };

  // Update lesson assignment status
  const updateLessonStatus = async (assignmentId, status) => {
    try {
      const response = await axios.put(createApiUrl(`/api/lesson-assignments/${assignmentId}/status`), {
        status
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Update local state
        setAssignedLessons(prev => 
          prev.map(lesson => 
            lesson._id === assignmentId 
              ? { ...lesson, status, completedDate: status === 'completed' ? new Date() : lesson.completedDate }
              : lesson
          )
        );
        setToast({
          message: 'Lesson status updated successfully!',
          type: 'success',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Error updating lesson status:', error);
      setToast({
        message: 'Failed to update lesson status. Please try again.',
        type: 'error',
        duration: 3000
      });
    }
  };

  // Update quiz assignment status
  const updateQuizStatus = async (quizId, status, data = {}) => {
    try {      
      // Find the quiz assignment ID for this quiz
      const quiz = assignedQuizzes.find(q => q._id === quizId);
      if (!quiz || !quiz.assignmentId) {
        console.error('Quiz assignment not found for quiz:', quizId);
        return;
      }
      // Prepare update payload
      const updatePayload = { status, ...data };
      
      const response = await axios.put(
        createApiUrl(`/api/quiz-assignments/${quiz.assignmentId}/status`),
        updatePayload,
        { withCredentials: true }
      );

      if (response.data.success) {
        // Update local state
        setAssignedQuizzes(prev => prev.map(quiz => 
          quiz._id === quizId ? { 
            ...quiz, 
            assignmentStatus: status,
            ...data
          } : quiz
        ));
        
      } else {
        console.error('Failed to update quiz status:', response.data.message);
      }
    } catch (error) {
      console.error('Error updating quiz status:', error);
    }
  };

  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      const authResponse = await axios.get(createApiUrl('/api/auth/check'));
      if (authResponse.data.user) {
        setUser(authResponse.data.user);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    
    const fetchData = async () => {
      try {
        // Get user data
        const authResponse = await axios.get(createApiUrl('/api/auth/check'));
        let userData = null;
        
        if (authResponse.data.user) {
          userData = authResponse.data.user;
          setUser(userData);
        }

        // Try to fetch missions, but don't let it break the dashboard
        try {
          const missionsResponse = await axios.get(createApiUrl('/api/mission-assignments/student'), {
            withCredentials: true
          });
          if (missionsResponse.data.success) {
            setMissions(missionsResponse.data.data);
          } else {
            setMissions([]);
          }
        } catch (missionsError) {
          console.error('Error fetching missions:', missionsError);
          setMissions([]); // Set empty array so dashboard still works
        }

        // Try to fetch messages, but don't let it break the dashboard
        try {
          const messagesResponse = await axios.get(createApiUrl('/api/student/messages'));
          if (messagesResponse.data.success) {
            setMessages(messagesResponse.data.data);
          }
        } catch (messagesError) {
                    // Don't set error state - just log it and continue
          setMessages([]); // Set empty array so dashboard still works
        }

        // Try to fetch assigned lessons, but don't let it break the dashboard
        try {
          const lessonsResponse = await axios.get(createApiUrl('/api/lesson-assignments/student'));
          if (lessonsResponse.data.success) {
            setAssignedLessons(lessonsResponse.data.data);
          }
        } catch (lessonsError) {
          // Don't set error state - just log it and continue
          setAssignedLessons([]); // Set empty array so dashboard still works
        }

        // Try to fetch assigned quizzes, but don't let it break the dashboard
        try {
          if (userData) {
            const quizzesResponse = await axios.get(createApiUrl(`/api/quiz-assignments/student/${userData._id}`), {
              withCredentials: true
            });
            if (quizzesResponse.data.success) {
              const studentQuizzes = quizzesResponse.data.assignments
                .map(assignment => {
                  return {
                    ...assignment.quizId,
                    assignmentId: assignment._id, // Add assignment ID for status updates
                    assignmentStatus: assignment.status || 'assigned',
                    assignedAt: assignment.assignedDate || assignment.createdAt,
                    completedAt: assignment.completedDate,
                    dueDate: assignment.dueDate,
                    score: assignment.score,
                    percentage: assignment.percentage,
                    attempts: assignment.attempts || 0,
                    maxAttempts: assignment.quizId.settings?.maxAttempts || 1
                  };
                })
                .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
              setAssignedQuizzes(studentQuizzes);
            } else {
              setAssignedQuizzes([]);
            }
          }
        } catch (quizzesError) {
          console.error('Error fetching assigned quizzes:', quizzesError);
          setAssignedQuizzes([]); // Set empty array so dashboard still works
        }


        // Only set loading to false if we have user data
        if (userData) {
          setLoading(false);
        } else {
          setError('Failed to load user data');
          setLoading(false);
        }
      } catch (error) {
                setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh user data every 30 seconds to get updated stars
  useEffect(() => {
    const interval = setInterval(() => {
      refreshUserData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Dashboard</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">Please try refreshing the page or contact support if the problem persists.</p>
        </div>
      </div>
    );
  }

  // Don't render BaseDashboard if we don't have user data yet
  if (!user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading user data...</span>
        </div>
        <p className="ms-3">Loading user data...</p>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}
      
      
    <FlexibleDashboard
      title="Student Dashboard"
      user={user}
      loading={loading}
      defaultActiveTab={activeTab}
      onTabChange={handleTabChange}
      banner={<ImpersonationBanner user={user} />}
      tabBadges={{
        messages: unreadMessageCount,
        missions: assignedMissionCount,
        assignedlessons: unreadAssignedLessonsCount,
        assignedquizzes: assignedQuizzesCount
      }}
    >
      <OverviewTab 
        tabId="overview"
        tabLabel="Overview"
        user={user} 
        missions={missions} 
        assignedLessons={assignedLessons}
        assignedQuizzes={assignedQuizzes}
        onViewMissions={handleViewMissions} 
        onViewProgress={handleViewProgress}
        onViewLessons={handleViewLessons}
        onViewQuizzes={() => handleTabChange('assignedquizzes')}
      />
      
      <MissionsTab 
        tabId="missions"
        tabLabel="My Missions"
        missions={missions}
        onMissionViewed={handleMissionViewed}
        onStartMission={handleStartMission}
      />
      
      <AssignedLessonsTab 
        tabId="assignedlessons"
        tabLabel="Assigned Lessons"
        assignedLessons={assignedLessons}
        loadingAssignedLessons={loadingAssignedLessons}
        onUpdateStatus={updateLessonStatus}
        onRefresh={fetchAssignedLessons}
      />
      
      <AssignedQuizzesTab 
        tabId="assignedquizzes"
        tabLabel="Assigned Quizzes"
        assignedQuizzes={assignedQuizzes}
        loadingAssignedQuizzes={loadingAssignedQuizzes}
        onUpdateStatus={updateQuizStatus}
        onRefresh={fetchAssignedQuizzes}
      />
      
      <ProgressTab 
        tabId="progress"
        tabLabel="Progress"
        user={user}
        missions={missions}
        lessons={assignedLessons}
        quizzes={assignedQuizzes}
      />
      
      <BadgesTab 
        tabId="badges"
        tabLabel="Badges"
        user={user}
        missions={missions}
        lessons={assignedLessons}
        quizzes={assignedQuizzes}
      />
      
      <MessagesTab 
        tabId="messages"
        tabLabel="Messages"
        messages={messages}
        onViewMessage={handleViewMessage}
        onSendReply={handleSendReply}
        selectedMessage={selectedMessage}
        replyText={replyText}
        setReplyText={setReplyText}
        sendingReply={sendingReply}
        showMessageModal={showMessageModal}
        closeMessageModal={closeMessageModal}
      />
      
      {/* <ResourcesTab 
        tabId="resources"
        tabLabel="Resources"
      /> */}
    </FlexibleDashboard>
    </>
  );
};

export default StudentDashboard; 